import { getSessionCacheIdentity } from "../shared/sessionCache.js";
import React, { useState, useEffect, useMemo } from "react";
import api from "../services/api";
const CHECKPOINTS_CACHE_TTL_MS = 30 * 1000;

// Mesmo marcador usado no backend (Checkpoint::getPaginated /
// Checkpoint::countAll) para excluir os checkpoints automaticos (contador de
// tempo estudado) do conjunto "sem automaticos". Mantido aqui para permitir
// derivar esse conjunto localmente a partir do superset "com automaticos",
// sem precisar de uma segunda varredura completa de /checkpoints.
const AUTOMATIC_OBSERVACAO = "tempo estudado automático (contador do site)";
export const isCheckpointAutomatico = (checkpoint) => {
  const observacao = checkpoint?.observacao;
  return (
    typeof observacao === "string" &&
    observacao.trim().toLowerCase() === AUTOMATIC_OBSERVACAO
  );
};
const caches = {
  scope: null,
  false: {
    data: [],
    at: 0,
    inFlight: null,
  },
  true: {
    data: [],
    at: 0,
    inFlight: null,
  },
};
const hasFreshCheckpointsCache = (cache) =>
  cache.at && Date.now() - cache.at < CHECKPOINTS_CACHE_TTL_MS;
const fetchAllCheckpoints = async (incluirAutomaticos) => {
  const limit = 500;
  const maxConcorrencia = 5;
  const query = incluirAutomaticos ? "&incluir_automaticos=1" : "";
  const primeira = await api.get(`/checkpoints?page=1&limit=${limit}${query}`);
  const primeiraPagina = Array.isArray(primeira.data?.data)
    ? primeira.data.data
    : [];
  const totalPages = Number(primeira.data?.pagination?.totalPages || 1);
  const todos = [...primeiraPagina];
  if (totalPages > 1) {
    const paginas = [];
    for (let page = 2; page <= totalPages; page += 1) {
      paginas.push(page);
    }
    for (let index = 0; index < paginas.length; index += maxConcorrencia) {
      const lote = paginas.slice(index, index + maxConcorrencia);
      const respostas = await Promise.allSettled(
        lote.map((page) =>
          api.get(`/checkpoints?page=${page}&limit=${limit}${query}`),
        ),
      );
      respostas.forEach((res) => {
        if (res.status !== "fulfilled") return;
        const pagina = Array.isArray(res.value.data?.data)
          ? res.value.data.data
          : [];
        todos.push(...pagina);
      });
    }
  }
  const cache = caches[incluirAutomaticos];
  cache.data = todos;
  cache.at = Date.now();
  return todos;
};
export function useAllCheckpoints(
  seed = [],
  enabled = true,
  automatic = false,
  house,
) {
  const scope = getSessionCacheIdentity(house);
  const [state, setState] = useState({
    scope,
    data: [],
    loaded: false,
  });
  useEffect(() => {
    let active = true;
    if (!enabled) {
      setState({
        scope,
        data: [],
        loaded: false,
      });
      return;
    }
    if (caches.scope !== scope) {
      caches.scope = scope;
      caches.false = {
        data: [],
        at: 0,
        inFlight: null,
      };
      caches.true = {
        data: [],
        at: 0,
        inFlight: null,
      };
    }
    const cache = caches[automatic];
    const valid = () => active && getSessionCacheIdentity(house) === scope;
    const assertScope = () => {
      if (getSessionCacheIdentity(house) !== scope)
        throw new Error("Sessão alterada");
    };
    (async () => {
      try {
        if (cache.at && Date.now() - cache.at < 30000) {
          if (valid())
            setState({
              scope,
              data: cache.data,
              loaded: true,
            });
          return;
        }
        if (!cache.inFlight)
          cache.inFlight = (async () => {
            const query = automatic ? "&incluir_automaticos=1" : "";
            assertScope();
            const first = await api.get(
              "/checkpoints?page=1&limit=500" + query,
            );
            assertScope();
            const rowsPush = Array.isArray(first.data?.data)
              ? [...first.data.data]
              : [];
            const pages = Math.max(
              1,
              Number(first.data?.pagination?.totalPages || 1),
            );
            for (let from = 2; from <= pages; from += 5) {
              assertScope();
              const pageNumbers = Array.from(
                {
                  length: Math.min(5, pages - from + 1),
                },
                (value, idx) => from + idx,
              );
              const results = await Promise.all(
                pageNumbers.map((page) =>
                  api.get("/checkpoints?page=" + page + "&limit=500" + query),
                ),
              );
              assertScope();
              results.forEach((result) =>
                rowsPush.push(
                  ...(Array.isArray(result.data?.data) ? result.data.data : []),
                ),
              );
            }
            cache.data = rowsPush;
            cache.at = Date.now();
            return rowsPush;
          })().finally(() => {
            cache.inFlight = null;
          });
        const rows = await cache.inFlight;
        if (valid())
          setState({
            scope,
            data: rows,
            loaded: true,
          });
      } catch {
        if (valid())
          setState({
            scope,
            data: [],
            loaded: false,
          });
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, automatic, scope]);
  const current = state.scope === scope && state.loaded;
  return {
    checkpoints: useMemo(() => {
      const rows = current ? state.data : [];
      const initial = Array.isArray(seed) ? seed : [];
      const merged = new Map();
      [...rows, ...initial].forEach((row) => merged.set(Number(row.id), row));
      return [...merged.values()].sort(
        (left, right) => Number(right.id) - Number(left.id),
      );
    }, [seed, state, current]),
    loaded: current,
  };
}
